import {FC, useState, useEffect} from 'react'
import {useDropzone} from 'react-dropzone'
import {ReactSortable} from 'react-sortablejs'
import uuid from 'react-uuid'
import styles from './ImageUpload.module.css'
interface FileWithPreview extends File {
  preview: string
  id: string | number
}

interface ImageUploadProps {
  label?: string
  iconImage?: string
  mainTextImage?: string
  descriptionImage?: string
  onChange?: (files: FileWithPreview[]) => void
  sizeImage?: string
  dragAndDropImage?: boolean
  verticalAlign?: boolean
  maxFiles?: number
  showThumbs?: boolean
  requiredWidth?: number
  requiredHeight?: number
  onSizeError?: (error: string) => void
}

export const ImageUpload: FC<ImageUploadProps> = ({
  label,
  onChange,
  iconImage,
  descriptionImage,
  mainTextImage = 'Arraste e solte sua imagem aqui',
  sizeImage,
  dragAndDropImage = false,
  maxFiles,
  showThumbs = true,
  requiredWidth = 640,
  requiredHeight = 640,
  onSizeError,
  verticalAlign,
}) => {
  const [files, setFiles] = useState<FileWithPreview[]>([])

  const {getRootProps, getInputProps} = useDropzone({
    accept: {
      'image/*': [],
    },
    maxFiles: maxFiles || undefined,
    onDrop: (acceptedFiles) => {
      if (maxFiles && files.length + acceptedFiles.length > maxFiles) {
        const remainingSlots = maxFiles - files.length
        acceptedFiles = acceptedFiles.slice(0, remainingSlots)
      }

      const newFiles = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          id: uuid(),
        })
      )

      if (newFiles.length > 0) {
        const updatedFiles = [...files, ...newFiles]
        showThumbs && setFiles(updatedFiles)
        if (onChange) {
          onChange(updatedFiles)
        }
      }
    },
  })

  const deleteImage = (id: any) => {
    const updatedFiles = files.filter((file) => file.id !== id)
    showThumbs && setFiles(updatedFiles)
    if (onChange) {
      onChange(updatedFiles)
    }
  }

  const handleSort = (newFiles: FileWithPreview[]) => {
    setFiles(newFiles)
    if (onChange) {
      onChange(newFiles)
    }
  }

  const thumbs = files.map((file, index) => (
    <div
      className={`
        ${styles.thumb}
        ${dragAndDropImage && index === 0 ? styles.firstThumb : ''}
        ${dragAndDropImage ? styles.draggableThumb : ''}
      `}
      key={file.id}
    >
      <div className={styles.thumbInner}>
        <div className={styles.thumbImgContainer}>
          <img
            src={file.preview}
            className={`${styles.thumbImg}`}
            onLoad={() => {
              URL.revokeObjectURL(file.preview)
            }}
          />
          <i
            className={`fas fa-times ${styles.iconThumbDelete}`}
            onClick={() => deleteImage(file.id)}
          ></i>
        </div>
        {dragAndDropImage && index === 0 && (
          <span className={styles.firstThumbLabel}>Principal</span>
        )}
      </div>
    </div>
  ))

  useEffect(() => {
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview))
  }, [files && showThumbs])

  return (
    <div>
      {label && <label className='form-label fw-normal'>{label}</label>}
      <section className='row px-3'>
        <div
          {...getRootProps({className: verticalAlign ? styles.verticalDropzone : styles.dropzone})}
        >
          <input {...getInputProps()} />
          {iconImage && <i className={`bi ${iconImage} ${styles.dropzoneIcon}`}></i>}
          {mainTextImage && <p className={styles.dropzoneMainText}>{mainTextImage}</p>}
          <p className={styles.dropzoneSubText}>
            Para maior qualidade envie imagens no formato JPG ou PNG
          </p>
          {sizeImage && <p className={styles.sizeImageText}>{sizeImage}</p>}
        </div>
        {showThumbs && (
          <>
            {dragAndDropImage ? (
              <ReactSortable<FileWithPreview>
                list={files}
                setList={handleSort}
                className={verticalAlign ? styles.verticalThumbContainer : styles.thumbsContainer}
                animation={150}
              >
                {thumbs}
              </ReactSortable>
            ) : (
              <aside
                className={verticalAlign ? styles.verticalThumbContainer : styles.thumbsContainer}
              >
                {thumbs}
              </aside>
            )}
            {thumbs.length > 0 && descriptionImage && (
              <div>
                <p className={styles.descriptionImage}>{descriptionImage}</p>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}
